import apiCall from './api';

/**
 * ✅ ATELIER: Service pour la gestion des fiches de mesures clientes.
 */
const measurementService = {

  /**
   * Liste des labels standards (pour affichage et itération).
   */
  STANDARD_FIELDS: [
    { key: 'tourPoitrine', label: 'Epaul', unit: 'cm' },
    { key: 'tourTaille', label: 'Pointrine', unit: 'cm' },
    { key: 'tourHanches', label: 'Taille', unit: 'cm' },
    { key: 'tourBassin', label: 'Ventre', unit: 'cm' },
    { key: 'longueurRobe', label: 'Tour de hanche', unit: 'cm' },
    { key: 'longueurManche', label: 'Pince', unit: 'cm' },
    { key: 'longueurJupe', label: 'Blouse', unit: 'cm' },
    { key: 'tourBras', label: 'Longueur robe', unit: 'cm' },
    { key: 'tourCuisse', label: 'Epaul manche', unit: 'cm' },
    { key: 'tourCou', label: 'Manche', unit: 'cm' },
    { key: 'carrureDos', label: 'Tour manche', unit: 'cm' },
    { key: 'hauteurEpaule', label: 'Longueur pagne', unit: 'cm' },
    { key: 'cou', label: 'Cou', unit: 'cm' },
    { key: 'enclure', label: 'Enclure', unit: 'cm' },
  ],

  /**
   * Récupérer tout l'historique des fiches d'une cliente.
   */
  async getMeasurementsByCustomer(customerId) {
    try {
      console.log('📏 Récupération mesures cliente:', customerId);

      const response = await apiCall.get(`/atelier/measurements/customer/${customerId}`);

      if (response.success) {
        const list = Array.isArray(response.data) ? response.data : [];
        console.log(`✅ ${list.length} fiche(s) trouvée(s)`);
        return { success: true, data: list };
      }

      return { success: false, data: [], message: response.message };

    } catch (error) {
      console.error('❌ Erreur récupération mesures:', error);
      return {
        success: false,
        data: [],
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Récupérer la fiche la plus récente d'une cliente.
   */
  async getLatestMeasurement(customerId) {
    try {
      const response = await apiCall.get(
        `/atelier/measurements/customer/${customerId}/latest`
      );
      return response;

    } catch (error) {
      console.error('❌ Erreur récupération dernière fiche:', error);
      return {
        success: false,
        data: null,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Récupérer une fiche par son ID.
   */
  async getMeasurementById(measurementId) {
    try {
      const response = await apiCall.get(`/atelier/measurements/${measurementId}`);
      return response;

    } catch (error) {
      console.error('❌ Erreur récupération fiche:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Créer une nouvelle fiche de mesures.
   */
  async createMeasurement(measurementData) {
    try {
      console.log('➕ Création fiche mesures:', measurementData.customerId);

      if (!measurementData.customerId) {
        return { success: false, message: 'La cliente est obligatoire' };
      }

      const payload = this._buildPayload(measurementData);

      const response = await apiCall.post('/atelier/measurements', payload);

      if (response.success) {
        console.log('✅ Fiche créée:', response.data?.id);
      }

      return response;

    } catch (error) {
      console.error('❌ Erreur création fiche:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Mettre à jour une fiche existante.
   */
  async updateMeasurement(measurementId, measurementData) {
    try {
      console.log('📝 Mise à jour fiche:', measurementId);

      const payload = this._buildPayload(measurementData);

      const response = await apiCall.put(
        `/atelier/measurements/${measurementId}`,
        payload
      );

      return response;

    } catch (error) {
      console.error('❌ Erreur mise à jour fiche:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Supprimer une fiche (soft delete).
   */
  async deleteMeasurement(measurementId) {
    try {
      console.log('🗑️ Suppression fiche:', measurementId);

      const response = await apiCall.delete(`/atelier/measurements/${measurementId}`);
      return response;

    } catch (error) {
      console.error('❌ Erreur suppression fiche:', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
 * ✅ Créer une fiche de mesures + cliente automatiquement
 * (cliente créée si elle n'existe pas, détectée par téléphone).
 */
async createMeasurementWithNewCustomer(data) {
  try {
    // Validation minimale
    if (!data.customerName?.trim()) {
      return { success: false, message: 'Le nom de la cliente est obligatoire' };
    }
    if (!data.customerPhone?.trim()) {
      return { success: false, message: 'Le téléphone est obligatoire' };
    }

    // Construction du payload
    const payload = {
      customerName: data.customerName.trim(),
      customerPhone: data.customerPhone.trim(),
      customerAddress: data.customerAddress?.trim() || null,
      customerEmail: data.customerEmail?.trim() || null,
      // Mesures standards
      tourPoitrine: this._toNumberOrNull(data.tourPoitrine),
      tourTaille: this._toNumberOrNull(data.tourTaille),
      tourHanches: this._toNumberOrNull(data.tourHanches),
      tourBassin: this._toNumberOrNull(data.tourBassin),
      longueurRobe: this._toNumberOrNull(data.longueurRobe),
      longueurManche: this._toNumberOrNull(data.longueurManche),
      longueurJupe: this._toNumberOrNull(data.longueurJupe),
      tourBras: this._toNumberOrNull(data.tourBras),
      tourCuisse: this._toNumberOrNull(data.tourCuisse),
      tourCou: this._toNumberOrNull(data.tourCou),
      carrureDos: this._toNumberOrNull(data.carrureDos),
      hauteurEpaule: this._toNumberOrNull(data.hauteurEpaule),
      cou: this._toNumberOrNull(data.cou),
      enclure: this._toNumberOrNull(data.enclure),
      customMeasurements: data.customMeasurements || {},
      notes: data.notes?.trim() || null
    };

    console.log('🧵 Création fiche + cliente avec payload:', payload);

    const response = await apiCall.post(
      '/atelier/measurements/with-new-customer',
      payload
    );

    return response;

  } catch (error) {
    console.error('❌ Erreur création fiche + cliente:', error);
    return {
      success: false,
      message: error.message || 'Erreur de connexion'
    };
  }
},

// Helper : convertir une valeur en nombre (ou null si vide)
_toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(value);
  return isNaN(num) ? null : num;
},

  // ===== UTILITAIRES D'AFFICHAGE =====

  /**
   * Formater une mesure pour l'affichage.
   */
  formatMeasurement(value, unit = 'cm') {
    if (value === null || value === undefined || value === '') return '—';
    return `${parseFloat(value)} ${unit}`;
  },

  /**
   * Compter le nombre de mesures renseignées dans une fiche.
   */
  countFilledMeasurements(measurement) {
    if (!measurement) return 0;

    let count = 0;
    this.STANDARD_FIELDS.forEach(f => {
      if (measurement[f.key] !== null && measurement[f.key] !== undefined) {
        count++;
      }
    });

    if (measurement.customMeasurements && typeof measurement.customMeasurements === 'object') {
      count += Object.keys(measurement.customMeasurements).length;
    }

    return count;
  },

  /**
   * Formater la date d'une fiche pour l'affichage.
   */
  formatDate(dateString) {
    if (!dateString) return 'Date inconnue';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  // ===== PRIVATE =====

  /**
   * Construit le payload en nettoyant les champs vides.
   */
  _buildPayload(data) {
    const payload = {
      customerId: data.customerId,
      notes: data.notes?.trim() || null,
      customMeasurements: data.customMeasurements || {}
    };

    // Ajouter les mesures standards (null si vide)
    this.STANDARD_FIELDS.forEach(f => {
      const value = data[f.key];
      if (value !== null && value !== undefined && value !== '') {
        payload[f.key] = parseFloat(value);
      } else {
        payload[f.key] = null;
      }
    });

    return payload;
  }
};

export default measurementService;